import { useCallback, useEffect, useState } from "react";
import { Ban, Plus, RefreshCw, ShieldCheck, ShieldOff, Trash2, Unlock } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";

const duration = (seconds) => {
  const value = Number(seconds || 0);
  if (value < 0) return "desbloqueio manual";
  if (value >= 86400) return `${Math.round(value / 86400)} dia(s)`;
  if (value >= 3600) return `${Math.round(value / 3600)} hora(s)`;
  return `${Math.round(value / 60)} minuto(s)`;
};

export default function SIPProtection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyIp, setBusyIp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [whitelistForm, setWhitelistForm] = useState({ ip: "", description: "" });

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get("/security/sip-blocks", { params: { _ts: Date.now() } });
      setData(response.data);
      setError("");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), 10000);
    return () => clearInterval(timer);
  }, [load]);

  const unblock = async (ip) => {
    if (!window.confirm(`Desbloquear o IP ${ip}?`)) return;
    setBusyIp(ip);
    setError("");
    setMessage("");
    try {
      await api.delete(`/security/sip-blocks/${encodeURIComponent(ip)}`);
      setMessage(`IP ${ip} desbloqueado.`);
      await load(true);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusyIp("");
    }
  };

  const addWhitelist = async (event, presetIp = "") => {
    event?.preventDefault?.();
    const payload = {
      ip: presetIp || whitelistForm.ip,
      description: presetIp ? "Liberado a partir de um bloqueio ativo" : whitelistForm.description,
    };
    if (!payload.ip) return;
    setBusyIp(payload.ip);
    setError("");
    setMessage("");
    try {
      await api.post("/security/sip-whitelist", payload);
      setWhitelistForm({ ip: "", description: "" });
      setMessage(`IP/rede ${payload.ip} adicionado a whitelist e desbloqueado.`);
      await load(true);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusyIp("");
    }
  };

  const removeWhitelist = async (ip) => {
    if (!window.confirm(`Remover ${ip} da whitelist? O IP podera ser bloqueado novamente.`)) return;
    setBusyIp(ip);
    setError("");
    setMessage("");
    try {
      await api.delete(`/security/sip-whitelist/${encodeURIComponent(ip)}`);
      setMessage(`${ip} removido da whitelist.`);
      await load(true);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusyIp("");
    }
  };

  return (
    <div className="space-y-5" data-testid="sip-protection-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Defesa de registro</div>
          <h1 className="text-2xl font-semibold tracking-tight">Protecao de login SIP</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">
            Consulte bloqueios SIP, libere acessos e mantenha IPs confiaveis na whitelist pelo painel admin.
          </p>
        </div>
        <button className="sbc-btn" onClick={() => load()} disabled={loading} title="Atualizar">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Atualizar
        </button>
      </div>

      {(message || error) && (
        <div className={`px-3 py-2 border text-xs ${error ? "border-red-400 bg-red-50 text-red-800" : "border-green-400 bg-green-50 text-green-800"}`}>
          {error || message}
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Estado</div>
          <div className="mt-2 flex items-center gap-2 font-semibold">
            {data?.enabled ? <ShieldCheck size={18} className="text-emerald-500" /> : <ShieldOff size={18} className="text-red-500" />}
            {data?.enabled ? "Protegido" : "Indisponivel"}
          </div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Limite</div>
          <div className="mt-2 text-xl font-mono">{data?.maxretry ?? 3} tentativas</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Janela</div>
          <div className="mt-2 text-xl font-mono">{duration(data?.findtime)}</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">IPs bloqueados</div>
          <div className="mt-2 text-xl font-mono">{data?.blocked?.length || 0}</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Descartes GeoIP</div>
          <div className="mt-2 text-xl font-mono">{data?.geoip_drop_count || 0}</div>
        </div>
      </div>

      <section className="sbc-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[color:var(--border-default)] flex items-center gap-2">
          <Ban size={15} />
          <h2 className="text-sm font-semibold">Bloqueios ativos</h2>
          <span className="text-xs text-[color:var(--text-muted)]">Expiracao automatica em {duration(data?.bantime)}</span>
        </div>
        <div className="overflow-auto">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>IP</th>
                <th>Motivo</th>
                <th>Protecao</th>
                <th>Ultimo ramal tentado</th>
                <th>Tentativas</th>
                <th>Ultima tentativa</th>
                <th className="text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={7} className="text-center py-8 text-[color:var(--text-muted)]">Carregando...</td></tr>
              ) : (data?.blocked || []).length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-[color:var(--text-muted)]">Nenhum IP bloqueado neste momento.</td></tr>
              ) : data.blocked.map((row) => (
                <tr key={row.ip}>
                  <td className="font-mono font-semibold">{row.ip}</td>
                  <td>{row.reason}</td>
                  <td className="font-mono text-xs">{(row.jails || []).join(", ") || data?.jail}</td>
                  <td className="font-mono">{row.last_extension || "-"}</td>
                  <td className="font-mono">{row.attempts || 3}</td>
                  <td className="font-mono text-xs">{row.last_seen || "-"}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button className="sbc-btn" onClick={() => unblock(row.ip)} disabled={busyIp === row.ip}>
                        <Unlock size={13} /> Desbloquear
                      </button>
                      <button className="sbc-btn sbc-btn-primary" onClick={(event) => addWhitelist(event, row.ip)} disabled={busyIp === row.ip}>
                        <ShieldCheck size={13} /> Whitelist
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sbc-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[color:var(--border-default)]">
          <h2 className="text-sm font-semibold">Whitelist permanente</h2>
          <p className="text-[10px] text-[color:var(--text-muted)] mt-1">
            IPs e redes CIDR desta lista nao serao bloqueados pela protecao SIP.
          </p>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-[minmax(180px,280px)_1fr_auto] gap-3 items-end" onSubmit={addWhitelist}>
          <label>
            <span className="text-[10px] uppercase text-[color:var(--text-muted)] block mb-1">IP ou rede CIDR</span>
            <input
              className="sbc-input font-mono"
              value={whitelistForm.ip}
              onChange={(event) => setWhitelistForm({ ...whitelistForm, ip: event.target.value })}
              placeholder="Ex: 170.83.153.207 ou 170.83.153.0/24"
              required
            />
          </label>
          <label>
            <span className="text-[10px] uppercase text-[color:var(--text-muted)] block mb-1">Descricao</span>
            <input
              className="sbc-input"
              value={whitelistForm.description}
              onChange={(event) => setWhitelistForm({ ...whitelistForm, description: event.target.value })}
              placeholder="Ex: Internet da filial Salvador"
            />
          </label>
          <button className="sbc-btn sbc-btn-primary" type="submit" disabled={Boolean(busyIp)}>
            <Plus size={13} /> Adicionar
          </button>
        </form>
        <div className="overflow-auto">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>IP / CIDR</th>
                <th>Descricao</th>
                <th>Status</th>
                <th className="text-right">Acao</th>
              </tr>
            </thead>
            <tbody>
              {(data?.whitelist || []).length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-[color:var(--text-muted)]">Nenhum IP cadastrado na whitelist.</td></tr>
              ) : data.whitelist.map((row) => (
                <tr key={row.ip}>
                  <td className="font-mono font-semibold">{row.ip}</td>
                  <td>{row.description || "-"}</td>
                  <td><span className="sbc-tag tag-online">Nunca bloquear</span></td>
                  <td className="text-right">
                    <button className="sbc-btn sbc-btn-danger" onClick={() => removeWhitelist(row.ip)} disabled={busyIp === row.ip}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
